// ============================================================================
// crmRascunhos · o texto que a recepção está escrevendo no CRM não some (27/10/2026)
// ============================================================================
// Queixa do dono: "o CRM a cada minuto apaga tudo e carrega de novo". O rascunho
// do Compositor vivia só no estado do componente: a recarga da página, a troca de
// conversa, de aba ou de vista no iPhone apagavam o texto.
//
// Aqui: um Map em memória por `${conversaId}:${modo}` ('resposta' | 'nota'),
// espelhado em sessionStorage (a aba do navegador, nunca localStorage: texto de
// cliente não fica no aparelho depois de fechar), com prazo de 24 h. A memória
// grava na hora; o espelho espera 300 ms sem digitar (debounce). Tudo em
// try/catch: sem sessionStorage (aba privada, cota cheia), ou com JSON quebrado,
// o CRM segue com a memória. Interruptor: CRM_SEM_RECARGA (featureFlags).
// ============================================================================

export const CHAVE = 'fk_crm_rascunhos';
export const TTL_MS = 24 * 60 * 60 * 1000;
export const DEBOUNCE_MS = 300;
export const MAX_TEXTO = 4000;
const MODOS = new Set(['resposta', 'nota']);

let memoria = null;   // Map chave -> { t: texto, em: ms }
let timer = null;

function armazem() {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null; } catch { return null; }
}

const chaveDe = (id, modo) => (id && MODOS.has(modo) ? `${id}:${modo}` : null);

function carregar(agora = Date.now()) {
  if (memoria) return memoria;
  memoria = new Map();
  const ss = armazem();
  if (!ss) return memoria;
  try {
    const cru = ss.getItem(CHAVE);
    const obj = cru ? JSON.parse(cru) : null;
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      for (const [k, v] of Object.entries(obj)) {
        if (!v || typeof v.t !== 'string' || !v.t || !Number.isFinite(v.em)) continue;
        if (agora - v.em > TTL_MS) continue;
        memoria.set(k, { t: v.t.slice(0, MAX_TEXTO), em: v.em });
      }
    }
  } catch { /* JSON quebrado: começa vazio (e o próximo espelho regrava limpo) */ }
  return memoria;
}

function espelhar() {
  timer = null;
  const ss = armazem();
  if (!ss || !memoria) return;
  try {
    if (!memoria.size) { ss.removeItem(CHAVE); return; }
    ss.setItem(CHAVE, JSON.stringify(Object.fromEntries(memoria)));
  } catch { /* cota cheia ou bloqueado: fica a memória */ }
}

function agendar() {
  if (timer) clearTimeout(timer);
  timer = setTimeout(espelhar, DEBOUNCE_MS);
}

/** Força o espelho agora (o envio com sucesso e o pagehide). */
export function gravarAgora() {
  if (timer) { clearTimeout(timer); timer = null; }
  espelhar();
}

export function lerRascunho(id, modo, agora = Date.now()) {
  const k = chaveDe(id, modo);
  if (!k) return '';
  const m = carregar(agora);
  const v = m.get(k);
  if (!v) return '';
  if (agora - v.em > TTL_MS) { m.delete(k); agendar(); return ''; }
  return v.t;
}

export function lerRascunhos(id, agora = Date.now()) {
  return { resposta: lerRascunho(id, 'resposta', agora), nota: lerRascunho(id, 'nota', agora) };
}

export function gravarRascunho(id, modo, texto, agora = Date.now()) {
  const k = chaveDe(id, modo);
  if (!k) return;
  const m = carregar(agora);
  const t = String(texto ?? '').slice(0, MAX_TEXTO);
  if (t.trim()) m.set(k, { t, em: agora });
  else m.delete(k);
  agendar();
}

export function limparRascunho(id, modo) {
  const k = chaveDe(id, modo);
  if (!k) return;
  carregar().delete(k);
  gravarAgora();
}

// Sair da conta (computador da recepção dividido): o próximo login na mesma aba não vê o texto de outra pessoa.
export function limparTodosRascunhos() {
  if (timer) { clearTimeout(timer); timer = null; }
  memoria = new Map();
  const ss = armazem();
  try { ss?.removeItem(CHAVE); } catch { /* bloqueado: a memória já está vazia */ }
}

export function temAlgumRascunho(agora = Date.now()) {
  for (const v of carregar(agora).values()) if (agora - v.em <= TTL_MS && v.t.trim()) return true;
  return false;
}

// a aba indo embora (recarga, troca de app no iPhone): o espelho não espera o debounce
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  try { window.addEventListener('pagehide', gravarAgora); } catch { /* ok */ }
}

// Só para testes: esquece a memória (simula o módulo carregando de novo depois de uma recarga).
export function _esquecerMemoria() {
  if (timer) { clearTimeout(timer); timer = null; }
  memoria = null;
}
